import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState, Booking, DisabledPrevButtonType } from './appTypes';
import {
  AvailableStore,
  NO_STORE_ID,
  Payload,
  Slot,
  TimeSlot,
} from '../../services/Appointment/model';
import { services } from '../../context';
import { getInitialSessionOnClientEntry } from '../../helpers/sessionHelper';
import { authTokenHandlerInstance } from '../../helpers/authTokenHelper';
import { getQueryParamsFromURL } from '../../helpers/urlHelper';

const DEFAULT_BOOKING: Booking = {
  orderNo: '',
  questionsToken: '',
  payOrderLink: '',
  payOrderLinkEnabled: true,
};

const initialState = (): AppState => {
  const restoredState = getInitialSessionOnClientEntry(SUB_PATH);
  if (!restoredState.selected.replacementService) {
    restoredState.selected.replacementService = {
      store: null,
      storeId: NO_STORE_ID,
      timeSlot: null,
    };
  }
  return {
    languages: {},
    isLoading: true,
    disabledPrevButton: restoredState.disabledPrevButton,
    operation: '',
    booking: restoredState.booking || DEFAULT_BOOKING,
    selected: {
      ...restoredState.selected,
    },
  };
};

const timeSlotExtractor = (
  slots: Slot[],
  timeSlotId: string,
): TimeSlot | undefined => {
  const extractedSlot = slots.find((slot) =>
    slot.timeSlots.some((timeSlot) => timeSlot.id === timeSlotId),
  );
  return extractedSlot?.timeSlots.find((slot) => slot.id === timeSlotId);
};

export const getLanguages = createAsyncThunk(
  'app/getLanguages',
  async (countryCode: string, { rejectWithValue }) => {
    try {
      const result =
        await services.languages.getLanguagesForCountryCode(countryCode);

      if (result.data?.languages && Array.isArray(result.data.languages)) {
        const { LANGUAGE } = CSS_CONFIG;
        const availableLanguages = result.data.languages.find(
          (lang: { code?: string }) => lang.code === LANGUAGE,
        );
        // Fall back to first available language if current language is not found
        return availableLanguages || result.data.languages[0] || {};
      }

      if (result.status === 404) {
        return {};
      }

      return {};
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  },
);

export const confirmAppointment = createAsyncThunk(
  'app/confirmAppointment',
  async (
    {
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
      selectedLanguage,
    }: {
      zipCode: string;
      serviceId: string;
      storeId: string;
      timeSlotId: string;
      from: string;
      serviceCode: string;
      payload: Payload;
      selectedLanguage: string | null;
    },
    { rejectWithValue },
  ) => {
    const { refApp } = getQueryParamsFromURL();
    const source = refApp ? 'partner' : 'customer';
    const { result, error } = await authTokenHandlerInstance.confirmAppointment(
      {
        zipCode,
        serviceId,
        storeId,
        timeSlotId,
        from,
        serviceCode,
        payload,
        selectedLanguage,
        source,
      },
    );

    if (error) {
      return rejectWithValue(error);
    }

    return {
      orderNo: result.orderNo as string,
      payOrderLink: result.payOrderLink as string,
      questionsToken: result.questionsToken as string,
      payOrderLinkEnabled: result.payOrderLinkEnabled as boolean,
    };
  },
);

const appSlice = createSlice({
  name: 'app',
  initialState: initialState(),
  reducers: {
    updateLoadingState(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    updateSelectedLanguage(state, action: PayloadAction<string>) {
      state.selected.language = action.payload;
    },
    updateSelectedService(
      state,
      action: PayloadAction<
        { serviceId: string; serviceCode: string } | undefined
      >,
    ) {
      state.selected.serviceId = action.payload?.serviceId || '';
      state.selected.serviceCode = action.payload?.serviceCode || '';
    },
    updateSelectedStore(state, action: PayloadAction<string>) {
      state.selected.storeId = action.payload;
      state.selected.timeSlot = null;
      state.selected.replacementService.timeSlot = null;
    },
    clearSelectedStore(state) {
      state.selected.storeId = NO_STORE_ID;
      state.selected.timeSlot = null;
      state.selected.replacementService.store = null;
      state.selected.replacementService.storeId = NO_STORE_ID;
      state.selected.replacementService.timeSlot = null;
    },
    updateSelectedReplacementServiceStore(
      state,
      action: PayloadAction<{ store: AvailableStore | null; storeId: string }>,
    ) {
      state.selected.replacementService.store = action.payload.store;
      state.selected.replacementService.storeId = action.payload.storeId;
    },
    updateSelectedTimeSlot(
      state,
      action: PayloadAction<{
        slots: Slot[];
        id: string;
        isReplacementTimeSlot: boolean;
      }>,
    ) {
      const timeSlot =
        timeSlotExtractor(action.payload.slots, action.payload.id) || null;
      if (action.payload.isReplacementTimeSlot) {
        state.selected.replacementService.timeSlot = timeSlot;
        state.selected.timeSlot = null;
      } else {
        state.selected.timeSlot = timeSlot;
        state.selected.replacementService.timeSlot = null;
      }
    },
    clearSelected(state) {
      state.selected = {
        serviceId: '',
        serviceCode: '',
        storeId: NO_STORE_ID,
        timeSlot: null,
        language: null,
        replacementService: {
          store: null,
          storeId: NO_STORE_ID,
          timeSlot: null,
        },
      };
    },
    clearSelectedTimeSlot(state) {
      state.selected = {
        ...state.selected,
        timeSlot: null,
        replacementService: {
          ...state.selected.replacementService,
          timeSlot: null,
        },
      };
    },
    clearBooking(state) {
      state.booking = DEFAULT_BOOKING;
    },
    setOperation(state, action: PayloadAction<string>) {
      state.operation = action.payload;
    },
    setDisabledPrevButton(
      state,
      action: PayloadAction<DisabledPrevButtonType>,
    ) {
      state.disabledPrevButton = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(confirmAppointment.fulfilled, (state, action) => {
      state.booking.orderNo = action.payload.orderNo;
      state.booking.payOrderLink = action.payload.payOrderLink;
      state.booking.questionsToken = action.payload.questionsToken;
      state.booking.payOrderLinkEnabled = action.payload.payOrderLinkEnabled;
    });
    builder.addCase(getLanguages.fulfilled, (state, action) => {
      state.languages = action.payload;
    });
    builder.addCase(getLanguages.rejected, (state) => {
      // Set empty object on failure to prevent undefined errors
      state.languages = {};
    });
  },
});

export const {
  updateLoadingState,
  updateSelectedLanguage,
  updateSelectedService,
  updateSelectedStore,
  updateSelectedTimeSlot,
  updateSelectedReplacementServiceStore,
  clearSelectedStore,
  clearSelectedTimeSlot,
  clearSelected,
  clearBooking,
  setOperation,
  setDisabledPrevButton,
} = appSlice.actions;

export default appSlice.reducer;
